import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { api, API } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, ArrowRight, Check, FileDown, Loader2, Sparkles } from "lucide-react";

/* /caregiver-tools/:slug — one free printable tool. Public, no gate.
 * Facebook link → this page → PDF download or the digital Memory Lane
 * version. Attribution is stashed so a later signup can be credited. */

function rememberAttribution(tool) {
    try {
        localStorage.setItem("ml_tool_attr", JSON.stringify({
            tool_day: tool.day, tool_name: tool.title, at: Date.now(),
        }));
    } catch { /* private mode */ }
}

export default function CaregiverToolPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [state, setState] = useState({ loading: true, tool: null, error: null });

    useEffect(() => {
        setState({ loading: true, tool: null, error: null });
        api.get(`/caregiver-tools/${slug}`)
            .then(r => {
                setState({ loading: false, tool: r.data, error: null });
                track("caregiver_tool_view", {
                    tool_day: r.data.day, tool_name: r.data.title,
                    referrer: document.referrer || "",
                });
                api.post(`/caregiver-tools/${slug}/event`, { kind: "view" }).catch(() => { });
            })
            .catch(() => setState({ loading: false, tool: null, error: "This tool isn't available yet." }));
    }, [slug]);

    if (state.loading) {
        return <main className="min-h-screen bg-cream flex items-center justify-center text-[#6E7A74]"><Loader2 className="animate-spin mr-2" /> Loading…</main>;
    }
    if (state.error) {
        return (
            <main className="min-h-screen bg-cream flex items-center justify-center px-4">
                <div className="ml-card p-8 max-w-md text-center" data-testid="caregiver-tool-error">
                    <p className="text-[#1F2624] font-medium">{state.error}</p>
                    <Link to="/caregiver-tools" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-dark">
                        <ArrowLeft size={14} /> See all caregiver tools
                    </Link>
                </div>
            </main>
        );
    }

    const t = state.tool;
    const socialImage = t.social_image
        ? (t.social_image.startsWith("http") ? t.social_image
            : t.social_image.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${t.social_image}`
                : `${window.location.origin}${t.social_image}`)
        : `${window.location.origin}/og/caregiver-tools.png`;

    const downloadHref = `${API}/caregiver-tools/${t.slug}/download`;
    const digitalHref = user && user.id
        ? (t.digital_url || "/dashboard")
        : `/login?utm_campaign=tool-day-${t.day}`;

    const onDownload = () => {
        track("caregiver_tool_download", { tool_day: t.day, tool_name: t.title, referrer: document.referrer || "" });
        rememberAttribution(t);
    };

    const onDigital = (e) => {
        track("caregiver_tool_digital_cta_click", { tool_day: t.day, tool_name: t.title, referrer: document.referrer || "" });
        rememberAttribution(t);
        api.post(`/caregiver-tools/${t.slug}/event`, { kind: "digital_cta_click" }).catch(() => { });
        // SPA navigation when React is live; the href itself keeps the button
        // working in the prerendered snapshot before hydration.
        e.preventDefault();
        navigate(digitalHref);
    };

    return (
        <main className="min-h-screen bg-cream">
            <SeoHead
                title={t.social_title || `Day ${t.day}: ${t.title} | Free Dementia Caregiver Tool`}
                description={t.social_description || t.short_description}
                canonicalPath={`/caregiver-tools/${t.slug}`}
                image={socialImage}
                imageAlt={`${t.title} — free dementia caregiver tool from Memory Lane`}
            />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
                <Link to="/caregiver-tools" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark mb-6" data-testid="caregiver-tool-back">
                    <ArrowLeft size={15} /> All caregiver tools
                </Link>

                <div>
                    <span className="inline-flex items-center rounded-full bg-teal-dark text-white text-xs font-semibold tracking-wider px-3.5 py-1.5" data-testid="caregiver-tool-day">
                        DAY {t.day}
                    </span>
                </div>
                <h1 className="mt-3 text-4xl sm:text-5xl font-heading font-semibold tracking-tight text-[#12776F]" data-testid="caregiver-tool-title">{t.title}</h1>
                <p className="mt-3 text-base md:text-lg text-[#4C5551] leading-relaxed" data-testid="caregiver-tool-description">{t.short_description}</p>

                <div className="mt-6 flex flex-wrap gap-3">
                    <a href={downloadHref} target="_blank" rel="noopener" onClick={onDownload}
                        className="ml-btn-primary" data-testid="caregiver-tool-download">
                        <FileDown size={17} /> Download Free Printable
                    </a>
                    <a href={digitalHref} onClick={onDigital}
                        className="ml-btn-secondary" data-testid="caregiver-tool-digital">
                        Use the Digital Version <ArrowRight size={16} />
                    </a>
                </div>
                <p className="mt-2.5 text-xs text-[#6E7A74]">Free to download — no account, email, or payment needed.</p>

                {(t.includes || []).length > 0 && (
                    <section className="ml-card p-6 md:p-7 mt-8" data-testid="caregiver-tool-includes">
                        <h2 className="font-heading text-base font-semibold text-[#1F2624] mb-3">What the printable covers</h2>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                            {t.includes.map((item, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-[#4C5551]">
                                    <Check size={15} className="text-teal-dark mt-0.5 shrink-0" strokeWidth={2.4} /> {item}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                <section className="mt-8 rounded-3xl border border-teal/30 bg-teal-soft/40 p-7 md:p-9 relative overflow-hidden" data-testid="caregiver-tool-digital-pitch">
                    <div aria-hidden="true" className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-teal-soft blur-2xl pointer-events-none" />
                    <div className="relative">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark flex items-center gap-1.5 mb-2">
                            <Sparkles size={13} strokeWidth={2.2} /> The living version
                        </p>
                        <h2 className="font-heading text-xl md:text-2xl font-semibold text-[#12776F]">
                            Want Memory Lane to remember the story for you?
                        </h2>
                        <div className="mt-3 space-y-3 text-[15px] text-[#2A3330] leading-relaxed max-w-xl">
                            <p>The printable is a great place to start. Memory Lane turns that same idea into a living, caregiver-owned record.</p>
                            <p>Enter the core information once, then keep it updated as things change — instead of starting from zero every time a new provider or service asks, <em>&ldquo;So, tell me what&apos;s been going on?&rdquo;</em></p>
                            <p>Memory Lane keeps your family&apos;s history and context together. You decide what gets shared, who sees it, and when.</p>
                        </div>
                        <div className="mt-5 flex flex-wrap items-center gap-3">
                            <a href={digitalHref} onClick={onDigital} className="ml-btn-primary" data-testid="caregiver-tool-create-record">
                                Create Your Caregiver-Owned Record
                            </a>
                            <span className="text-sm font-medium text-[#4C5551]">Free for family caregivers.</span>
                        </div>
                    </div>
                </section>

                <footer className="mt-10 pt-5 border-t border-cream-dark text-xs text-[#6E7A74]">
                    Part of Memory Lane&apos;s &ldquo;30 Days of Things Nobody Tells You About Dementia Care.&rdquo; Memory Lane is a caregiver-support tool, not a medical record.
                </footer>
            </div>
        </main>
    );
}
